import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HttpError, request } from "../../api/http";
import { useAuth } from "../../context/AuthContext";
import {
  ITALIAN_RENEWAL_STATUSES,
  ItalianRenewalRow,
  ItalianRenewalStatus,
  QuiereRenovar,
} from "../../types/renewals";
import StatusBadge from "../OnboardingCustomers/components/StatusBadge";
import Pagination from "../OnboardingCustomers/components/Pagination";
import QuiereRenovarSelect from "./components/QuiereRenovarSelect";
import RenewalExpandedRow from "./components/RenewalExpandedRow";

type ApiResponse = {
  rows: ItalianRenewalRow[];
  total: number;
  limit: number;
  offset: number;
};

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_LABEL: Record<ItalianRenewalStatus, string> = {
  complete: "Complete",
  canceled: "Canceled",
};

const fmtDate = (d: string | null) => {
  if (!d) return "—";
  const part = d.substring(0, 10);
  const [y, m, day] = part.split("-");
  if (!y || !m || !day) return d;
  return `${day}/${m}/${y}`;
};

const fmtDateTime = (d: string | null) => {
  if (!d) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(d));
  } catch {
    return d;
  }
};

export default function RenovacionesItalia() {
  const { token } = useAuth();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<ItalianRenewalStatus[]>([
    ...ITALIAN_RENEWAL_STATUSES,
  ]);
  const [renewalYear, setRenewalYear] = useState<string>("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [rows, setRows] = useState<ItalianRenewalRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    selectedStatuses.forEach((s) => params.append("status[]", s));
    if (debouncedSearch) params.set("q", debouncedSearch);
    if (renewalYear) params.set("renewalYear", renewalYear);
    params.set("limit", String(pageSize));
    params.set("offset", String(page * pageSize));

    request<ApiResponse>(
      `/api/admin/operations/renewals-it?${params.toString()}`,
      { token },
    )
      .then((data) => {
        if (cancelled) return;
        setRows(data.rows);
        setTotal(data.total);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof HttpError
            ? `HTTP ${err.status}`
            : "No se pudo cargar el listado",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, selectedStatuses, debouncedSearch, renewalYear, page, pageSize]);

  const handleSearch = (v: string) => {
    setSearch(v);
    setPage(0);
  };
  const handleRenewalYear = (v: string) => {
    setRenewalYear(v);
    setPage(0);
  };
  const handlePageSize = (v: number) => {
    setPageSize(v);
    setPage(0);
  };
  const toggleStatus = (s: ItalianRenewalStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
    setPage(0);
  };
  const toggleExpand = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const patchRow = (
    leadUlid: string,
    renewalYear: number,
    patch: Partial<ItalianRenewalRow>,
  ) => {
    setRows((prev) =>
      prev.map((r) =>
        r.leadUlid === leadUlid && r.renewalYear === renewalYear
          ? { ...r, ...patch }
          : r,
      ),
    );
  };

  const updateQuiereRenovar = async (
    row: ItalianRenewalRow,
    next: QuiereRenovar | null,
  ) => {
    const prev = row.quiereRenovar;
    patchRow(row.leadUlid, row.renewalYear, { quiereRenovar: next });
    try {
      await request<unknown>(
        `/api/admin/operations/renewals-it/${row.leadUlid}/${row.renewalYear}/ops-note`,
        { method: "PUT", body: { quiereRenovar: next }, token },
      );
    } catch {
      patchRow(row.leadUlid, row.renewalYear, { quiereRenovar: prev });
    }
  };

  return (
    <div style={{ padding: "1.5rem 2rem" }}>
      <div style={{ marginBottom: "1rem" }}>
        <h2 style={{ margin: 0, fontSize: "1.15rem", color: "#2A3F54" }}>
          Renovaciones Italia
        </h2>
        <p
          style={{
            margin: "0.25rem 0 0",
            color: "#73879C",
            fontSize: ".85rem",
          }}
        >
          Renovaciones de pólizas IT — click en una fila para ver todos los
          datos y editar las notas operativas.
        </p>
      </div>

      <div
        style={{
          background: "white",
          padding: "1rem 1.25rem",
          borderRadius: "0.6rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <input
          type="search"
          placeholder="Buscar lead_id, email, póliza, customer…"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ flex: "1 1 260px", minWidth: 240 }}
        />
        <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
          {ITALIAN_RENEWAL_STATUSES.map((s) => {
            const active = selectedStatuses.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                style={{
                  padding: "0.35rem 0.7rem",
                  borderRadius: "999px",
                  border: active ? "1px solid #0069d9" : "1px solid #dee2e6",
                  background: active ? "#0069d9" : "white",
                  color: active ? "white" : "#1f2933",
                  fontSize: ".75rem",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                {STATUS_LABEL[s]}
              </button>
            );
          })}
        </div>
        <select
          value={renewalYear}
          onChange={(e) => handleRenewalYear(e.target.value)}
          style={{ minWidth: 140 }}
          title="Filtrar por año de renovación"
        >
          <option value="">Todos los años</option>
          {[1, 2, 3, 4, 5].map((y) => (
            <option key={y} value={y}>
              Año {y}
            </option>
          ))}
        </select>
        <div
          style={{
            marginLeft: "auto",
            fontSize: ".8rem",
            color: "#73879C",
          }}
        >
          {rows.length} de {total}
        </div>
      </div>

      {error && (
        <div
          style={{
            background: "#fde2e2",
            color: "#b32424",
            padding: "0.75rem 1rem",
            borderRadius: "0.5rem",
            marginBottom: "1rem",
            fontSize: ".85rem",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          background: "white",
          borderRadius: "0.6rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          overflow: "hidden",
          opacity: loading ? 0.6 : 1,
          transition: "opacity 0.15s ease",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: ".85rem",
            }}
          >
            <thead>
              <tr style={{ background: "#f8f9fb" }}>
                <Th style={{ width: 32 }}></Th>
                <Th>Customer</Th>
                <Th>Email</Th>
                <Th>Póliza / Año</Th>
                <Th>Lead ID</Th>
                <Th>Estado</Th>
                <Th>Fecha renov.</Th>
                <Th>Acept.</Th>
                <Th title="Días que quedan al cliente para aceptar la renovación. El cliente puede renovar hasta 15 días después de la fecha de renovación.">
                  Días acept.
                </Th>
                <Th>Quiere reno?</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const key = `${r.leadUlid}_${r.renewalYear}`;
                const isExpanded = expanded.has(key);
                return (
                  <RowView
                    key={key}
                    row={r}
                    expanded={isExpanded}
                    onToggle={() => toggleExpand(key)}
                    onQuiereRenovarChange={(next) => updateQuiereRenovar(r, next)}
                    onContactoSaved={(value) =>
                      patchRow(r.leadUlid, r.renewalYear, {
                        contactoMariana: value,
                      })
                    }
                  />
                );
              })}
              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    style={{
                      padding: "2rem",
                      textAlign: "center",
                      color: "#73879C",
                    }}
                  >
                    No hay renovaciones que coincidan con los filtros.
                  </td>
                </tr>
              )}
              {loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    style={{
                      padding: "2rem",
                      textAlign: "center",
                      color: "#73879C",
                    }}
                  >
                    Cargando…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={handlePageSize}
        disabled={loading}
      />
    </div>
  );
}

function DaysRemaining({ value }: { value: number | null }) {
  if (null === value) {
    return <span style={{ color: "#adb5bd" }}>—</span>;
  }
  if (value < 0) {
    return (
      <span
        title="Plazo de renovación expirado"
        style={{ color: "#b32424", fontWeight: 600 }}
      >
        {value}
      </span>
    );
  }
  if (value <= 3) {
    return (
      <span title="Quedan pocos días" style={{ color: "#856404", fontWeight: 600 }}>
        {value}
      </span>
    );
  }
  return <span style={{ color: "#1f2933" }}>{value}</span>;
}

function Th({
  children,
  style,
  title,
}: {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  title?: string;
}) {
  return (
    <th
      title={title}
      style={{
        textAlign: "left",
        padding: "0.65rem 0.75rem",
        fontSize: ".7rem",
        textTransform: "uppercase",
        color: "#73879C",
        fontWeight: 600,
        letterSpacing: "0.5px",
        borderBottom: "1px solid #e9ecef",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  mono,
  style,
  onClick,
}: {
  children: React.ReactNode;
  mono?: boolean;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent<HTMLTableCellElement>) => void;
}) {
  return (
    <td
      onClick={onClick}
      style={{
        padding: "0.65rem 0.75rem",
        verticalAlign: "middle",
        fontFamily: mono
          ? "ui-monospace, SFMono-Regular, Menlo, monospace"
          : undefined,
        fontSize: mono ? ".8rem" : undefined,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </td>
  );
}

function RowView({
  row,
  expanded,
  onToggle,
  onQuiereRenovarChange,
  onContactoSaved,
}: {
  row: ItalianRenewalRow;
  expanded: boolean;
  onToggle: () => void;
  onQuiereRenovarChange: (next: QuiereRenovar | null) => void;
  onContactoSaved: (value: string | null) => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        style={{
          cursor: "pointer",
          background: expanded ? "#f0f7ff" : "white",
          borderBottom: "1px solid #f1f3f5",
        }}
      >
        <Td>
          <span
            style={{
              display: "inline-block",
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.15s ease",
              color: "#73879C",
              fontSize: ".75rem",
            }}
          >
            ▶
          </span>
        </Td>
        <Td>{row.customer}</Td>
        <Td mono>{row.email}</Td>
        <Td mono>{row.polizaYear}</Td>
        <Td mono>
          <Link
            to={`/leads/${row.leadUlid}`}
            onClick={(e) => e.stopPropagation()}
            style={{
              color: "#0069d9",
              textDecoration: "none",
              fontWeight: 600,
            }}
            title="Abrir ficha de lead"
          >
            {row.leadId}
          </Link>
        </Td>
        <Td>
          <StatusBadge status={row.status} />
        </Td>
        <Td>{fmtDate(row.renewalDate)}</Td>
        <Td>{fmtDateTime(row.renewalAcceptanceAt)}</Td>
        <Td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
          <DaysRemaining value={row.daysUntilAcceptance} />
        </Td>
        <Td onClick={(e) => e.stopPropagation()}>
          <QuiereRenovarSelect
            value={row.quiereRenovar}
            onChange={onQuiereRenovarChange}
          />
        </Td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={10} style={{ padding: 0 }}>
            <RenewalExpandedRow row={row} onContactoSaved={onContactoSaved} />
          </td>
        </tr>
      )}
    </>
  );
}
